// myutms/src/pages/PricingPresentationAdminPage.jsx
 // src/pages/PricingPresentationAdminPage.jsx

 import React, { useCallback, useEffect, useMemo, useState } from "react";
 import {
   Alert,
   Autocomplete,
   Box,
   Button,
   Card,
   CardContent,
   Checkbox,
   Chip,
   CircularProgress,
   Divider,
   FormControlLabel,
   Grid,
   List,
   ListItem,
   ListItemText,
   Paper,
   Snackbar,
   Stack,
   Tab,
   Tabs,
   TextField,
   Typography,
 } from "@mui/material";

 import AddIcon from "@mui/icons-material/Add";
 import SaveIcon from "@mui/icons-material/Save";
 import RefreshIcon from "@mui/icons-material/Refresh";
 import WarningAmberIcon from "@mui/icons-material/WarningAmber";
 import CategoryIcon from "@mui/icons-material/Category";
 import ViewListIcon from "@mui/icons-material/ViewList";
 import LayersIcon from "@mui/icons-material/Layers";
 import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

 import { useAuth } from "../contexts/AuthContext";
 import LoadingScreen from "../components/LoadingScreen/LoadingScreen";

 /**
  * IMPORTANT:
  * If your app exports `db` from a different file, update ONLY this import.
  * Common alternatives in many apps:
  *   - ../firebaseConfig
  *   - ../firebase/firebase
  *   - ../config/firebase
  */
 import { db } from "../firebase";

 import {
   initializePricingPresentationDoc,
   getNormalizedPricingPresentationDoc,
   savePricingPresentationDraft,
   PRICING_PRESENTATION_ALLOWED_EDITOR_ROLES,
 } from "../utils/pricingPresentationFirestore";

import {
  PRICING_PRESENTATION_PLAN_KEYS,
  createDefaultFeatureRecord,
  createDefaultPricingPresentationDoc,
  normalizePricingPresentationDoc,
  validatePricingPresentationDoc,
} from "plan-catalog";

 /* ──────────────────────────────────────────────────────────────
  * Local helpers
  * ─────────────────────────────────────────────────────────── */

 function clone(value) {
   return JSON.parse(JSON.stringify(value));
 }

 function byAlpha(a, b) {
   return String(a || "").localeCompare(String(b || ""));
 }

 function roleFromAuth(auth) {
   return (
     auth?.userProfile?.role ||
     auth?.userProfile?.userRole ||
     auth?.effectiveUser?.role ||
     auth?.currentUser?.role ||
     null
   );
 }

 function actorFromAuth(auth) {
   return {
     uid: auth?.currentUser?.uid || null,
     email: auth?.currentUser?.email || null,
     role: roleFromAuth(auth),
   };
 }

  /**
  * Normalize admin text fields without interfering with normal typing.
  *
  * Why:
  * - preserve internal spaces while the user types
  * - strip only leading/trailing whitespace when the field is committed
  * - keep labels/tooltips clean without making the inputs feel broken
  */
 function normalizeAdminText(value) {
   return typeof value === "string" ? value.trim() : "";
 }

 /**
  * Identify auto-generated placeholder feature keys created by the current admin UI.
  *
  * Why:
  * - We only want to auto-rename placeholder keys like "new_feature_2"
  *   into descriptive slugs.
  * - We do NOT want to silently rename already-curated keys that may be
  *   referenced in cardFeatureIds or other saved content.
  */
 function isAutoGeneratedFeatureKey(featureId) {
   return /^new_feature_\d+$/i.test(String(featureId || "").trim());
 }

 /**
  * Convert a feature label into a stable slug-like feature key.
  *
  * Example:
  *   "Google Analytics UTM Builder" -> "google_analytics_utm_builder"
  *
  * Notes:
  * - Lowercases
  * - Replaces non-alphanumeric separators with underscores
  * - Collapses repeated underscores
  * - Falls back to "feature" if the label is empty after normalization
  */
 function createFeatureKeyFromLabel(label) {
   const normalized = normalizeAdminText(label)
     .toLowerCase()
     .replace(/[^a-z0-9]+/g, "_")
     .replace(/^_+|_+$/g, "")
     .replace(/_+/g, "_");

   return normalized || "feature";
 }

 /**
  * Ensure a generated feature key is unique within the current feature map.
  *
  * Why:
  * - Two labels can normalize to the same slug.
  * - We preserve the current key if it already matches the candidate.
  * - Otherwise we suffix _2, _3, etc. until the key is unique.
  */
 function getUniqueFeatureKey(candidateKey, existingKeys, currentKey = "") {
   const cleanCandidate = String(candidateKey || "").trim() || "feature";
   const current = String(currentKey || "").trim();
   const taken = new Set((existingKeys || []).filter(Boolean));

   if (!taken.has(cleanCandidate) || cleanCandidate === current) {
     return cleanCandidate;
   }

   let idx = 2;
   let nextKey = `${cleanCandidate}_${idx}`;

   while (taken.has(nextKey) && nextKey !== current) {
     idx += 1;
     nextKey = `${cleanCandidate}_${idx}`;
   }

   return nextKey;
 }

 /**
  * Normalize a category-key input so we persist only the actual compare-category key.
  *
  * Supports:
  * - exact key matches
  * - display strings like "Label (actual_key)"
  * - corrupted values that still contain a known key somewhere in the string
  *
  * Why:
  * - The autocomplete should save only category keys such as
  *   "campaign_url_builders"
  * - Older/bad values like
  *   "Campaign URL Builders for Google Analytics & Adobe Analytics (campaign_url_builders)"
  *   must be collapsed back to the real key
  */
 function normalizeCategoryKeyValue(rawValue, categoryOptions = []) {
   const raw = normalizeAdminText(rawValue);
   if (!raw) return "";

   const validKeys = (categoryOptions || [])
     .map((option) => String(option?.key || "").trim())
     .filter(Boolean);

   if (validKeys.includes(raw)) {
     return raw;
   }

   const parenMatch = raw.match(/\(([a-z0-9_]+)\)\s*$/i);
   if (parenMatch?.[1] && validKeys.includes(parenMatch[1])) {
     return parenMatch[1];
   }

   const includedKey = validKeys.find((key) => raw.includes(key));
   if (includedKey) {
     return includedKey;
   }

   return raw;
 }

 /**
  * Normalize all feature category keys in a pricing-presentation doc against
  * the currently defined compare categories.
  *
  * Why:
  * - Repairs previously saved bad categoryKey values
  * - Keeps the admin UI and generated compare payload aligned
  */
 function normalizeDraftCategoryKeys(doc) {
   const next = clone(doc);
   const categoryOptions = Array.isArray(next?.compareCategories)
     ? next.compareCategories.map((category) => ({
         key: String(category?.key || ""),
         label: String(category?.label || ""),
       }))
     : [];

   Object.keys(next?.features || {}).forEach((featureId) => {
     const currentValue = next.features?.[featureId]?.categoryKey || "";
     next.features[featureId].categoryKey = normalizeCategoryKeyValue(
       currentValue,
       categoryOptions,
     );
   });

   return next;
 }

 function formatUpdatedBy(updatedBy) {
   if (!updatedBy || typeof updatedBy !== "object") return "Unknown";
   return updatedBy.email || updatedBy.uid || "Unknown";
 }

 function TabPanel(props) {
   const { children, value, index } = props;

   return (
     <div
       role="tabpanel"
       hidden={value !== index}
       id={`pricing-presentation-tabpanel-${index}`}
       aria-labelledby={`pricing-presentation-tab-${index}`}
     >
       {value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null}
     </div>
   );
 }

 /* ──────────────────────────────────────────────────────────────
  * Component
  * ─────────────────────────────────────────────────────────── */

 export default function PricingPresentationAdminPage() {
   const auth = useAuth();
   const actor = useMemo(() => actorFromAuth(auth), [auth]);
   const actorRole = actor.role;

   const isAllowed = useMemo(
     () => PRICING_PRESENTATION_ALLOWED_EDITOR_ROLES.includes(actorRole),
     [actorRole],
   );

   const [loading, setLoading] = useState(true);
   const [initializing, setInitializing] = useState(false);
   const [saving, setSaving] = useState(false);

   const [sourceDoc, setSourceDoc] = useState(null);
   const [draft, setDraft] = useState(null);

   const [activeTab, setActiveTab] = useState(0);
   const [selectedFeatureId, setSelectedFeatureId] = useState("");
   const [snackbar, setSnackbar] = useState({
     open: false,
     severity: "success",
     message: "",
   });

   const validation = useMemo(() => {
     if (!draft) {
       return { ok: true, errors: [] };
     }
     return validatePricingPresentationDoc(draft);
   }, [draft]);

   const dirty = useMemo(() => {
     if (!sourceDoc || !draft) return false;
     return JSON.stringify(sourceDoc) !== JSON.stringify(draft);
   }, [sourceDoc, draft]);

   const featureIds = useMemo(() => {
     if (!draft?.features) return [];
     return Object.keys(draft.features).sort(byAlpha);
   }, [draft]);

   const selectedFeature = useMemo(() => {
     if (!selectedFeatureId || !draft?.features) return null;
     return draft.features[selectedFeatureId] || null;
   }, [draft, selectedFeatureId]);

   const compareCategories = useMemo(() => {
     if (!Array.isArray(draft?.compareCategories)) return [];
     return [...draft.compareCategories].sort(
       (a, b) => Number(a.order || 0) - Number(b.order || 0),
     );
   }, [draft]);

   const compareCategoryOptions = useMemo(() => {
     return compareCategories
       .map((category) => ({
         key: String(category?.key || ""),
         label: String(category?.label || ""),
       }))
       .filter((category) => category.key);
   }, [compareCategories]);

   const notify = useCallback((severity, message) => {
     setSnackbar({
       open: true,
       severity,
       message,
     });
   }, []);

   const loadDoc = useCallback(async () => {
     if (!isAllowed) {
       setLoading(false);
       return;
     }

     setLoading(true);

     try {
       const normalized = await getNormalizedPricingPresentationDoc({
         db,
         actor,
       });

       const repaired = normalizeDraftCategoryKeys(normalized);

       setSourceDoc(repaired);
       setDraft(clone(repaired));

       const firstFeatureId = Object.keys(repaired.features || {}).sort(byAlpha)[0] || "";
       setSelectedFeatureId(firstFeatureId);
     } catch (error) {
       console.error("[PricingPresentationAdminPage] Failed to load doc:", error);
       notify("error", error.message || "Failed to load pricing presentation document.");
     } finally {
       setLoading(false);
     }
   }, [actor, isAllowed, notify]);

   useEffect(() => {
     loadDoc();
   }, [loadDoc]);

   const handleInitializeDefaults = useCallback(async () => {
     if (!isAllowed) return;

     setInitializing(true);

     try {
       const result = await initializePricingPresentationDoc({
         db,
         actor,
         seedDoc: createDefaultPricingPresentationDoc(),
       });

       const repaired = normalizeDraftCategoryKeys(result.doc);

       setSourceDoc(repaired);
       setDraft(clone(repaired));

       const firstFeatureId = Object.keys(repaired.features || {}).sort(byAlpha)[0] || "";
       setSelectedFeatureId(firstFeatureId);

       notify(
         "success",
         result.created
           ? "Pricing presentation document initialized."
           : "Pricing presentation document already existed.",
       );
     } catch (error) {
       console.error("[PricingPresentationAdminPage] Failed to initialize doc:", error);
       notify("error", error.message || "Failed to initialize pricing presentation document.");
     } finally {
       setInitializing(false);
     }
   }, [actor, isAllowed, notify]);

   const handleResetDraft = useCallback(() => {
     if (!sourceDoc) return;
     setDraft(clone(sourceDoc));
     notify("success", "Draft reset to the latest saved version.");
   }, [sourceDoc, notify]);

   const handleSaveDraft = useCallback(async () => {
     if (!draft || !isAllowed) return;

     setSaving(true);

     try {
       const normalized = normalizePricingPresentationDoc(draft);
       const repaired = normalizeDraftCategoryKeys(normalized);

       const saved = await savePricingPresentationDraft({
         db,
         actor,
         nextDoc: repaired,
       });

       setSourceDoc(saved);
       setDraft(clone(saved));

       notify("success", "Pricing presentation draft saved.");
     } catch (error) {
       console.error("[PricingPresentationAdminPage] Failed to save draft:", error);
       notify("error", error.message || "Failed to save pricing presentation draft.");
     } finally {
       setSaving(false);
     }
   }, [actor, draft, isAllowed, notify]);

   const updateDraft = useCallback((updater) => {
     setDraft((prev) => {
       const base = prev ? clone(prev) : createDefaultPricingPresentationDoc();
       const next = updater(base);

       /**
        * IMPORTANT:
        * Do NOT normalize on every keystroke.
        *
        * Why:
        * - normalizePricingPresentationDoc() trims strings
        * - trimming during typing breaks normal text entry
        * - users must be able to type spaces naturally in subtitle, best-for,
        *   feature label, feature intro, and tooltip fields
        *
        * We keep the draft "raw" while editing and normalize only when:
        * - explicitly committing a field onBlur
        * - saving the draft to Firestore
        */
       return next;
     });
   }, []);

   const handlePlanFieldChange = useCallback((planKey, field, value) => {
     updateDraft((next) => {
       next.planContent = next.planContent || {};
       next.planContent[planKey] = next.planContent[planKey] || {
         subtitle: "",
         bestFor: "",
         featureIntro: "Included features:",
         cardFeatureIds: [],
       };

       next.planContent[planKey][field] = value;
       return next;
     });
   }, [updateDraft]);

   const handlePlanCardFeatureIdsChange = useCallback((planKey, ids) => {
     updateDraft((next) => {
       next.planContent = next.planContent || {};
       next.planContent[planKey] = next.planContent[planKey] || {
         subtitle: "",
         bestFor: "",
         featureIntro: "Included features:",
         cardFeatureIds: [],
       };

       next.planContent[planKey].cardFeatureIds = ids;
       return next;
     });
   }, [updateDraft]);

   const handleSelectFeature = useCallback((featureId) => {
     setSelectedFeatureId(featureId);
   }, []);

   const handleFeatureFieldChange = useCallback((featureId, field, value) => {
     let renamedFeatureId = null;

     updateDraft((next) => {
       next.features = next.features || {};
       next.features[featureId] =
         next.features[featureId] || createDefaultFeatureRecord(featureId);

       if (field === "categoryKey") {
         next.features[featureId][field] = normalizeCategoryKeyValue(
           value,
           next.compareCategories || [],
         );
       } else {
         next.features[featureId][field] = value;
       }

       /**
        * Auto-rename placeholder feature keys from the edited label.
        *
        * Behavior:
        * - Only applies to auto-generated placeholder keys like "new_feature_2"
        * - Only runs when the label field changes
        * - Updates any existing planContent.cardFeatureIds references
        * - Keeps the feature selected after the rename
        *
        * Why:
        * - Makes the feature registry and Card Feature IDs much easier to manage
        * - Prevents the admin UI from filling up with opaque placeholder keys
        */
       if (field === "label") {
         const trimmedLabel = normalizeAdminText(value);
         const currentKey = String(featureId || "").trim();

         if (trimmedLabel && isAutoGeneratedFeatureKey(currentKey)) {
           const candidateKey = createFeatureKeyFromLabel(trimmedLabel);
           const nextKey = getUniqueFeatureKey(
             candidateKey,
             Object.keys(next.features || {}),
             currentKey,
           );

           if (nextKey !== currentKey) {
             const currentFeatureRecord = next.features[currentKey];
             delete next.features[currentKey];
             next.features[nextKey] = currentFeatureRecord;

             Object.keys(next.planContent || {}).forEach((planKey) => {
               const ids = Array.isArray(next.planContent?.[planKey]?.cardFeatureIds)
                 ? next.planContent[planKey].cardFeatureIds
                 : [];

               next.planContent[planKey].cardFeatureIds = ids.map((id) =>
                 id === currentKey ? nextKey : id,
               );
             });

             renamedFeatureId = nextKey;
           }
         }
       }

       return next;
     });

     if (renamedFeatureId) {
       setSelectedFeatureId(renamedFeatureId);
       notify("success", `Feature key renamed to "${renamedFeatureId}".`);
     }
   }, [notify, updateDraft]);

   const handleFeatureIncludedToggle = useCallback((featureId, planKey, checked) => {
     updateDraft((next) => {
       next.features = next.features || {};
       next.features[featureId] =
         next.features[featureId] || createDefaultFeatureRecord(featureId);

       next.features[featureId].includedIn =
         next.features[featureId].includedIn || {};

       next.features[featureId].includedIn[planKey] = checked;
       return next;
     });
   }, [updateDraft]);

   const handleAddFeature = useCallback(() => {
     const baseId = "new_feature";
     const existing = new Set(featureIds);

     let idx = 1;
     let nextId = `${baseId}_${idx}`;
     while (existing.has(nextId)) {
       idx += 1;
       nextId = `${baseId}_${idx}`;
     }

     updateDraft((next) => {
       next.features = next.features || {};
       next.features[nextId] = createDefaultFeatureRecord(nextId);
       return next;
     });

     setSelectedFeatureId(nextId);
     notify("success", `Added feature "${nextId}".`);
   }, [featureIds, notify, updateDraft]);

   const handleRemoveSelectedFeature = useCallback(() => {
     if (!selectedFeatureId) return;

     updateDraft((next) => {
       next.features = next.features || {};
       delete next.features[selectedFeatureId];

       Object.keys(next.planContent || {}).forEach((planKey) => {
         const ids = Array.isArray(next.planContent?.[planKey]?.cardFeatureIds)
           ? next.planContent[planKey].cardFeatureIds
           : [];
         next.planContent[planKey].cardFeatureIds = ids.filter(
           (id) => id !== selectedFeatureId,
         );
       });

       return next;
     });

     const remaining = featureIds.filter((id) => id !== selectedFeatureId);
     setSelectedFeatureId(remaining[0] || "");
     notify("success", `Removed feature "${selectedFeatureId}".`);
   }, [featureIds, notify, selectedFeatureId, updateDraft]);

   const handleAddCompareCategory = useCallback(() => {
     const existing = new Set(compareCategories.map((c) => c.key));
     let idx = 1;
     let key = `new_category_${idx}`;

     while (existing.has(key)) {
       idx += 1;
       key = `new_category_${idx}`;
     }

     updateDraft((next) => {
       next.compareCategories = Array.isArray(next.compareCategories)
         ? next.compareCategories
         : [];

       next.compareCategories.push({
         key,
         label: `New Category ${idx}`,
         order: next.compareCategories.length + 1,
       });

       return next;
     });

     notify("success", `Added compare category "${key}".`);
   }, [compareCategories, notify, updateDraft]);

   const handleCompareCategoryFieldChange = useCallback((index, field, value) => {
     updateDraft((next) => {
       next.compareCategories = Array.isArray(next.compareCategories)
         ? next.compareCategories
         : [];

       if (!next.compareCategories[index]) return next;
       next.compareCategories[index][field] = value;
       return next;
     });
   }, [updateDraft]);

   const handleRemoveCompareCategory = useCallback((index) => {
     updateDraft((next) => {
       next.compareCategories = Array.isArray(next.compareCategories)
         ? next.compareCategories
         : [];

       next.compareCategories.splice(index, 1);
       return next;
     });

     notify("success", "Compare category removed.");
   }, [notify, updateDraft]);

   if (loading) {
     return <LoadingScreen />;
   }

   if (!isAllowed) {
     return (
       <Box sx={{ p: 4 }}>
         <Alert severity="error">
           You do not have access to Pricing Presentation Admin.
         </Alert>
       </Box>
     );
   }

   if (!draft) {
     return (
       <Box sx={{ p: 4 }}>
         <Stack spacing={3}>
           <Typography variant="h4" sx={{ fontWeight: 700 }}>
             Pricing Presentation Admin
           </Typography>

           <Alert severity="warning">
             No pricing presentation document was found yet.
           </Alert>

           <Box>
             <Button
               variant="contained"
               startIcon={initializing ? <CircularProgress size={18} /> : <AddIcon />}
               onClick={handleInitializeDefaults}
               disabled={initializing}
             >
               {initializing ? "Initializing…" : "Initialize Defaults"}
             </Button>
           </Box>
         </Stack>
       </Box>
     );
   }

   return (
     <Box sx={{ p: { xs: 2, md: 3 } }}>
       <Stack spacing={3}>
         <Box>
           <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
             Pricing Presentation Admin
           </Typography>

           <Typography variant="body1" color="text.secondary">
             Manage the presentation/content contract used for pricing cards,
             compare tables, tooltips, and plan messaging. Billing-critical
             data such as Stripe price IDs and entitlements remain in code.
           </Typography>
         </Box>

         <Paper
           variant="outlined"
           sx={{
             p: 2,
             borderRadius: 3,
           }}
         >
           <Stack
             direction={{ xs: "column", md: "row" }}
             spacing={2}
             alignItems={{ xs: "flex-start", md: "center" }}
             justifyContent="space-between"
           >
             <Stack spacing={0.5}>
               <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                 Status
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Editor role: <strong>{actorRole || "unknown"}</strong>
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Last updated by: <strong>{formatUpdatedBy(sourceDoc.updatedBy)}</strong>
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 Contract version: <strong>{draft.version}</strong>
               </Typography>
             </Stack>

             <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
               <Button
                 variant="outlined"
                 startIcon={<RefreshIcon />}
                 onClick={handleResetDraft}
                 disabled={!dirty || saving}
               >
                 Reset Draft
               </Button>

               <Button
                 variant="contained"
                 startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                 onClick={handleSaveDraft}
                 disabled={saving || !dirty || !validation.ok}
               >
                 {saving ? "Saving…" : "Save Draft"}
               </Button>
             </Stack>
           </Stack>
         </Paper>

         {!validation.ok && (
           <Alert severity="warning" icon={<WarningAmberIcon />}>
             <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
               Validation issues detected
             </Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
               Compare Order controls feature order within a category. Lower numbers
               appear first. Category Key should match one of the compare categories.
             </Typography>
             <List dense disablePadding>
               {validation.errors.map((error, idx) => (
                 <ListItem key={`${error}-${idx}`} sx={{ py: 0 }}>
                   <ListItemText primary={error} />
                 </ListItem>
               ))}
             </List>
           </Alert>
         )}

         <Paper
           variant="outlined"
           sx={{
             borderRadius: 3,
             overflow: "hidden",
           }}
         >
           <Tabs
             value={activeTab}
             onChange={(_, next) => setActiveTab(next)}
             variant="scrollable"
             scrollButtons="auto"
           >
             <Tab icon={<InfoOutlinedIcon />} iconPosition="start" label="Overview" />
             <Tab icon={<LayersIcon />} iconPosition="start" label="Plan Content" />
             <Tab icon={<ViewListIcon />} iconPosition="start" label="Features" />
             <Tab icon={<CategoryIcon />} iconPosition="start" label="Compare Categories" />
           </Tabs>

           <Box sx={{ p: { xs: 2, md: 3 } }}>
             <TabPanel value={activeTab} index={0}>
               <Grid container spacing={2}>
                 <Grid item xs={12} md={6}>
                   <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                     <CardContent>
                       <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                         How this page works
                       </Typography>

                       <Typography variant="body2" color="text.secondary" paragraph>
                         This admin surface manages only the pricing presentation/content
                         contract used by WordPress and future app-side pricing displays.
                       </Typography>

                       <List dense>
                         <ListItem>
                           <ListItemText
                             primary="Plan Content"
                             secondary="Subtitle, best-for copy, feature-intro copy, and card feature ordering per plan."
                           />
                         </ListItem>
                         <ListItem>
                           <ListItemText
                             primary="Features"
                             secondary="Reusable feature registry: label, tooltip, category, compare order, and included plans."
                           />
                         </ListItem>
                         <ListItem>
                           <ListItemText
                             primary="Compare Categories"
                             secondary="Display order and labels for compare-table category groups."
                           />
                         </ListItem>
                       </List>
                     </CardContent>
                   </Card>
                 </Grid>

                 <Grid item xs={12} md={6}>
                   <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                     <CardContent>
                       <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                         Enterprise safeguards
                       </Typography>

                       <List dense>
                         <ListItem>
                           <ListItemText
                             primary="Role-gated editing"
                             secondary="Only superAppadmin can currently read/write this contract."
                           />
                         </ListItem>
                         <ListItem>
                           <ListItemText
                             primary="Billing stays in code"
                             secondary="Stripe price IDs, entitlements, self-serve policy, and seat limits are not edited here."
                           />
                         </ListItem>
                         <ListItem>
                           <ListItemText
                             primary="Draft-first workflow"
                             secondary="This page saves a normalized draft document that later merge/publish flows can consume."
                           />
                         </ListItem>
                       </List>
                     </CardContent>
                   </Card>
                 </Grid>
               </Grid>
             </TabPanel>

             <TabPanel value={activeTab} index={1}>
               <Stack spacing={3}>
                 {PRICING_PRESENTATION_PLAN_KEYS.map((planKey) => {
                   const plan = draft.planContent?.[planKey] || {
                     subtitle: "",
                     bestFor: "",
                     featureIntro: "",
                     cardFeatureIds: [],
                   };

                   return (
                     <Card key={planKey} variant="outlined" sx={{ borderRadius: 3 }}>
                       <CardContent>
                         <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                           {planKey.charAt(0).toUpperCase() + planKey.slice(1)} Plan Content
                         </Typography>

                         <Grid container spacing={2}>
                           <Grid item xs={12} md={4}>
                             <TextField
                               fullWidth
                               label="Subtitle"
                               value={plan.subtitle || ""}
                               onChange={(e) =>
                                 handlePlanFieldChange(planKey, "subtitle", e.target.value)
                               }
                               onBlur={(e) =>
                                 handlePlanFieldChange(
                                   planKey,
                                   "subtitle",
                                   normalizeAdminText(e.target.value),
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12} md={4}>
                             <TextField
                               fullWidth
                               label="Best For"
                               value={plan.bestFor || ""}
                               onChange={(e) =>
                                 handlePlanFieldChange(planKey, "bestFor", e.target.value)
                               }
                               onBlur={(e) =>
                                 handlePlanFieldChange(
                                   planKey,
                                   "bestFor",
                                   normalizeAdminText(e.target.value),
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12} md={4}>
                             <TextField
                               fullWidth
                               label="Feature Intro"
                               value={plan.featureIntro || ""}
                               onChange={(e) =>
                                 handlePlanFieldChange(planKey, "featureIntro", e.target.value)
                               }
                               onBlur={(e) =>
                                 handlePlanFieldChange(
                                   planKey,
                                   "featureIntro",
                                   normalizeAdminText(e.target.value),
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12}>
                             <Autocomplete
                               multiple
                               options={featureIds}
                               value={Array.isArray(plan.cardFeatureIds) ? plan.cardFeatureIds : []}
                               onChange={(_, nextValue) =>
                                 handlePlanCardFeatureIdsChange(planKey, nextValue)
                               }
                               renderTags={(value, getTagProps) =>
                                 value.map((option, index) => {
                                   const featureLabel =
                                     draft.features?.[option]?.label || option;

                                   return (
                                     <Chip
                                       variant="outlined"
                                       label={featureLabel}
                                       {...getTagProps({ index })}
                                       key={`${planKey}-${option}`}
                                     />
                                   );
                                 })
                               }
                               renderInput={(params) => (
                                 <TextField
                                   {...params}
                                   label="Card Feature IDs"
                                   placeholder="Select features shown in pricing cards"
                                   helperText="This controls which features appear in the pricing card for this plan. Compare-table changes alone do not automatically add features to cards."
                                 />
                               )}
                             />
                           </Grid>
                         </Grid>
                       </CardContent>
                     </Card>
                   );
                 })}
               </Stack>
             </TabPanel>

             <TabPanel value={activeTab} index={2}>
               <Grid container spacing={2}>
                 <Grid item xs={12} md={4}>
                   <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
                     <CardContent>
                       <Stack
                         direction="row"
                         justifyContent="space-between"
                         alignItems="center"
                         sx={{ mb: 2 }}
                       >
                         <Typography variant="h6" sx={{ fontWeight: 700 }}>
                           Features
                         </Typography>

                         <Button
                           size="small"
                           startIcon={<AddIcon />}
                           onClick={handleAddFeature}
                         >
                           Add Feature
                         </Button>
                       </Stack>

                       <List
                         sx={{
                           maxHeight: 520,
                           overflowY: "auto",
                           border: "1px solid",
                           borderColor: "divider",
                           borderRadius: 2,
                         }}
                       >
                         {featureIds.map((featureId) => {
                           const active = featureId === selectedFeatureId;
                           const featureLabel = draft.features?.[featureId]?.label || "";

                           return (
                             <ListItem
                               key={featureId}
                               button
                               selected={active}
                               onClick={() => handleSelectFeature(featureId)}
                               sx={{
                                 borderBottom: "1px solid",
                                 borderColor: "divider",
                               }}
                             >
                               <ListItemText
                                 primary={featureLabel || featureId}
                                 secondary={featureId}
                               />
                             </ListItem>
                           );
                         })}
                       </List>
                     </CardContent>
                   </Card>
                 </Grid>

                 <Grid item xs={12} md={8}>
                   <Card variant="outlined" sx={{ borderRadius: 3 }}>
                     <CardContent>
                       {!selectedFeatureId || !selectedFeature ? (
                         <Typography color="text.secondary">
                           Select a feature to edit.
                         </Typography>
                       ) : (
                         <Stack spacing={2.5}>
                           <Stack
                             direction={{ xs: "column", sm: "row" }}
                             justifyContent="space-between"
                             alignItems={{ xs: "flex-start", sm: "center" }}
                             spacing={1}
                           >
                             <Box>
                               <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                 Edit Feature: {selectedFeature?.label || selectedFeatureId}
                               </Typography>
                               <Typography variant="body2" color="text.secondary">
                                 Feature Key: {selectedFeatureId}
                               </Typography>
                             </Box>

                             <Button
                               color="error"
                               variant="outlined"
                               onClick={handleRemoveSelectedFeature}
                             >
                               Remove Feature
                             </Button>
                           </Stack>

                           <Grid container spacing={2}>
                             <Grid item xs={12} md={6}>
                               <TextField
                                 fullWidth
                                 label="Feature Label"
                                 value={selectedFeature.label || ""}
                                 onChange={(e) =>
                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "label",
                                     e.target.value,
                                   )
                                 }
                                 onBlur={(e) =>
                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "label",
                                     normalizeAdminText(e.target.value),
                                   )
                                 }
                                 helperText="Shown in compare rows and pricing cards when this feature is included in a card. Placeholder keys like new_feature_2 will automatically rename from this label."
                               />
                             </Grid>

                             <Grid item xs={12} md={6}>
                               <Autocomplete
                                 freeSolo
                                 options={compareCategoryOptions}
                                 value={
                                   compareCategoryOptions.find(
                                     (option) =>
                                       option.key === normalizeCategoryKeyValue(
                                         selectedFeature.categoryKey || "",
                                         compareCategoryOptions,
                                       ),
                                   ) || null
                                 }
                                 onChange={(_, nextValue) => {
                                   const nextKey =
                                     typeof nextValue === "string"
                                       ? nextValue
                                       : nextValue?.key || "";

                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "categoryKey",
                                     nextKey,
                                   );
                                 }}
                                 onInputChange={(_, nextInputValue, reason) => {
                                   if (reason === "input") {
                                     handleFeatureFieldChange(
                                       selectedFeatureId,
                                       "categoryKey",
                                       nextInputValue,
                                     );
                                   }
                                 }}
                                 getOptionLabel={(option) =>
                                   typeof option === "string"
                                     ? option
                                     : option.key
                                 }
                                 isOptionEqualToValue={(option, value) =>
                                   option.key === value.key
                                 }
                                 renderOption={(props, option) => (
                                   <li {...props} key={option.key}>
                                     {option.label} ({option.key})
                                   </li>
                                 )}
                                 renderInput={(params) => (
                                   <TextField
                                     {...params}
                                     fullWidth
                                     label="Category Key"
                                     helperText="Choose an existing compare category key or type one manually. Only the actual key is saved."
                                   />
                                 )}
                               />
                             </Grid>

                             <Grid item xs={12}>
                               <TextField
                                 fullWidth
                                 multiline
                                 minRows={3}
                                 label="Tooltip"
                                 value={selectedFeature.tooltip || ""}
                                 onChange={(e) =>
                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "tooltip",
                                     e.target.value,
                                   )
                                 }
                                 onBlur={(e) =>
                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "tooltip",
                                     normalizeAdminText(e.target.value),
                                   )
                                 }
                                 helperText="Plain-text tooltip shown on hover/focus in pricing cards and compare rows."
                               />
                             </Grid>

                             <Grid item xs={12} md={4}>
                               <TextField
                                 fullWidth
                                 type="number"
                                 label="Compare Order"
                                 value={selectedFeature.compareOrder ?? 0}
                                 onChange={(e) =>
                                   handleFeatureFieldChange(
                                     selectedFeatureId,
                                     "compareOrder",
                                     Number(e.target.value || 0),
                                   )
                                 }
                                 helperText="Lower numbers appear first within the selected compare category. Use gaps like 10, 20, 30."
                               />
                             </Grid>
                           </Grid>

                           <Divider />

                           <Box>
                             <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                               Included In Plans
                             </Typography>

                             <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                               {PRICING_PRESENTATION_PLAN_KEYS.map((planKey) => (
                                 <FormControlLabel
                                   key={`${selectedFeatureId}-${planKey}`}
                                   control={
                                     <Checkbox
                                       checked={
                                         selectedFeature?.includedIn?.[planKey] === true
                                       }
                                       onChange={(e) =>
                                         handleFeatureIncludedToggle(
                                           selectedFeatureId,
                                           planKey,
                                           e.target.checked,
                                         )
                                       }
                                     />
                                   }
                                   label={planKey}
                                 />
                               ))}
                             </Stack>
                           </Box>
                         </Stack>
                       )}
                     </CardContent>
                   </Card>
                 </Grid>
               </Grid>
             </TabPanel>

             <TabPanel value={activeTab} index={3}>
               <Card variant="outlined" sx={{ borderRadius: 3 }}>
                 <CardContent>
                   <Stack
                     direction={{ xs: "column", sm: "row" }}
                     justifyContent="space-between"
                     alignItems={{ xs: "flex-start", sm: "center" }}
                     spacing={1}
                     sx={{ mb: 2 }}
                   >
                     <Typography variant="h6" sx={{ fontWeight: 700 }}>
                       Compare Categories
                     </Typography>

                     <Button
                       size="small"
                       startIcon={<AddIcon />}
                       onClick={handleAddCompareCategory}
                     >
                       Add Category
                     </Button>
                   </Stack>

                   <Stack spacing={2}>
                     {compareCategories.map((category, index) => (
                       <Paper
                         key={`${category.key}-${index}`}
                         variant="outlined"
                         sx={{ p: 2, borderRadius: 2 }}
                       >
                         <Grid container spacing={2} alignItems="center">
                           <Grid item xs={12} md={4}>
                             <TextField
                               fullWidth
                               label="Key"
                               value={category.key || ""}
                               onChange={(e) =>
                                 handleCompareCategoryFieldChange(
                                   index,
                                   "key",
                                   e.target.value,
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12} md={4}>
                             <TextField
                               fullWidth
                               label="Label"
                               value={category.label || ""}
                               onChange={(e) =>
                                 handleCompareCategoryFieldChange(
                                   index,
                                   "label",
                                   e.target.value,
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12} md={2}>
                             <TextField
                               fullWidth
                               type="number"
                               label="Order"
                               value={category.order ?? 0}
                               onChange={(e) =>
                                 handleCompareCategoryFieldChange(
                                   index,
                                   "order",
                                   Number(e.target.value || 0),
                                 )
                               }
                             />
                           </Grid>

                           <Grid item xs={12} md={2}>
                             <Button
                               color="error"
                               variant="outlined"
                               fullWidth
                               onClick={() => handleRemoveCompareCategory(index)}
                             >
                               Remove
                             </Button>
                           </Grid>
                         </Grid>
                       </Paper>
                     ))}
                   </Stack>
                 </CardContent>
               </Card>
             </TabPanel>
           </Box>
         </Paper>
       </Stack>

       <Snackbar
         open={snackbar.open}
         autoHideDuration={3500}
         onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
         anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
       >
         <Alert
           severity={snackbar.severity}
           onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
           variant="filled"
           sx={{ width: "100%" }}
         >
           {snackbar.message}
         </Alert>
       </Snackbar>
     </Box>
   );
 }